import { dataProvider } from "@/providers/dataProvider";
import {
  malformedGuidesResponse,
  parseGuideEditorSnapshot,
  parseGuideListPage,
  parseGuideStaleSummary,
} from "./parse";
import type {
  GuideCityOption,
  GuideEditorSnapshot,
  GuideListPage,
  GuideListQuery,
  GuideStaleSummary,
} from "./types";

export const GUIDES_API_BASE = "/v1/admin/guides";
export const GUIDES_INVALID_ARGUMENT_CODE = "client.invalid_argument";

export function serializeGuidesQuery(
  query: Readonly<Record<string, string | number | boolean | undefined>>,
): string {
  const params = new URLSearchParams();
  for (const key of Object.keys(query).sort()) {
    const value = query[key];
    if (value === undefined || value === false) continue;
    const text = String(value);
    if (text.trim() === "") continue;
    params.set(key, text);
  }
  return params.toString();
}

async function readGuidesData(url: string): Promise<unknown> {
  if (dataProvider.custom === undefined) {
    throw {
      code: GUIDES_INVALID_ARGUMENT_CODE,
      message: "Custom requests unavailable",
    };
  }

  const result = await dataProvider.custom({ url, method: "get" });
  if (result.data === undefined || result.data === null) {
    throw malformedGuidesResponse("data");
  }
  return result.data;
}

export async function listGuides(
  query: GuideListQuery,
): Promise<GuideListPage> {
  const queryString = serializeGuidesQuery({
    cityId: query.cityId,
    cursor: query.cursor,
    limit: query.limit ?? 25,
    q: query.q?.trim(),
    stale: query.stale,
    status: query.status,
  });
  const url =
    queryString === ""
      ? GUIDES_API_BASE
      : `${GUIDES_API_BASE}?${queryString}`;
  return parseGuideListPage(await readGuidesData(url));
}

export async function getGuideEditorSnapshot(
  guideId: string,
): Promise<GuideEditorSnapshot> {
  if (guideId === "") {
    throw {
      code: GUIDES_INVALID_ARGUMENT_CODE,
      message: "Empty guide id",
    };
  }

  const snapshot = parseGuideEditorSnapshot(
    await readGuidesData(`${GUIDES_API_BASE}/${encodeURIComponent(guideId)}`),
  );
  if (snapshot.id !== guideId) {
    throw malformedGuidesResponse("guide.id");
  }
  return snapshot;
}

export async function listGuideCityOptions(): Promise<GuideCityOption[]> {
  const cities = new Map<string, GuideCityOption>();
  const seenCursors = new Set<string>();
  let cursor: string | undefined;

  do {
    const page = await listGuides({ cursor, limit: 100 });
    for (const item of page.items) {
      cities.set(item.city.cityId, {
        cityId: item.city.cityId,
        name: item.city.name,
      });
    }
    cursor = page.nextCursor ?? undefined;
    if (cursor !== undefined) {
      if (seenCursors.has(cursor)) {
        throw malformedGuidesResponse("nextCursor");
      }
      seenCursors.add(cursor);
    }
  } while (cursor !== undefined);

  return [...cities.values()].sort(
    (left, right) =>
      left.name.localeCompare(right.name) ||
      left.cityId.localeCompare(right.cityId),
  );
}

export async function getGuideStaleSummary(): Promise<GuideStaleSummary> {
  return parseGuideStaleSummary(
    await readGuidesData(`${GUIDES_API_BASE}/stale/summary`),
  );
}
